/**
 * 경량 Markdown → HTML 변환기 (법적 문서 렌더링 전용).
 *
 * 지원: 제목(#~######), 굵게(**), 이탤릭(*), 인라인코드(`), 링크([t](u)),
 *       순서/비순서 목록, 인용(>), 수평선(---), GFM 표(| a | b |), 문단.
 * 보안: 원문을 먼저 HTML 이스케이프한 뒤 인라인 마크만 태그로 치환 → 임의 HTML 주입 불가.
 *       링크 href는 http/https/mailto/상대경로만 허용(javascript: 등 차단).
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function safeHref(url: string): string | null {
  const u = url.trim()
  if (/^(https?:\/\/|mailto:|\/)/i.test(u)) return u
  if (/^[\w./#?=&%-]+$/.test(u)) return u // 상대경로/앵커
  return null
}

function inline(text: string): string {
  let s = escapeHtml(text)
  // 인라인 코드
  s = s.replace(/`([^`]+)`/g, '<code>$1</code>')
  // 링크 [text](url) — 내부(/terms 등)·외부(lettaga.com 등) 구분 없이 전부 같은 탭에서 이동.
  // (내부/외부에 따라 새 탭 여부가 갈리면 문서 읽는 도중 링크 동작이 들쭉날쭉해 보이므로 통일)
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, t, u) => {
    const href = safeHref(u)
    if (!href) return t
    return `<a href="${href}">${t}</a>`
  })
  // 굵게 **text**
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  // 이탤릭 *text* (굵게 처리 후 남은 단일 *)
  s = s.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, '$1<em>$2</em>')
  return s
}

function renderTable(rows: string[]): string {
  // rows: 헤더줄, 구분줄(---), 본문줄...
  const parseCells = (line: string) =>
    line.replace(/^\||\|$/g, '').split('|').map((c) => c.trim())
  const header = parseCells(rows[0])
  const body = rows.slice(2).map(parseCells)
  let html = '<table><thead><tr>'
  html += header.map((c) => `<th>${inline(c)}</th>`).join('')
  html += '</tr></thead><tbody>'
  for (const r of body) {
    html += '<tr>' + r.map((c) => `<td>${inline(c)}</td>`).join('') + '</tr>'
  }
  html += '</tbody></table>'
  return html
}

export interface RenderMarkdownOptions {
  /**
   * 문서 최상단의 '# 제목' 줄만 잘라낸다(제목/버전을 이미 감싸는 UI — 모달 헤더, 카드 타이틀 —
   * 가 별도로 표시하는 화면에서 제목이 중복 출력되는 것을 막기 위함). Operated by/UEN/주소·
   * Last updated 등 그 아래 레터헤드 내용은 화면상 유일한 출처이므로 그대로 유지한다.
   */
  stripLeadingLetterhead?: boolean
}

export function renderMarkdown(md: string, options?: RenderMarkdownOptions): string {
  let src = (md || '').replace(/\r\n/g, '\n')
  let letterheadHtml = ''
  if (options?.stripLeadingLetterhead) {
    const allLines = src.split('\n')
    if (/^#\s+/.test(allLines[0] || '')) {
      let idx = 1
      while (idx < allLines.length && allLines[idx].trim() === '') idx++
      // 제목 다음, 첫 빈 줄 전까지의 줄(Operated by/UEN/주소, Last updated)을
      // 별도 톤(작은 글씨·은은한 색)의 레터헤드 블록으로 분리해 렌더링한다.
      const letterheadLines: string[] = []
      while (idx < allLines.length && allLines[idx].trim() !== '') {
        letterheadLines.push(allLines[idx])
        idx++
      }
      if (letterheadLines.length) {
        letterheadHtml = `<div class="legal-letterhead">${letterheadLines.map((l) => `<p>${inline(l)}</p>`).join('')}</div>`
      }
      src = allLines.slice(idx).join('\n')
    }
  }
  const lines = src.split('\n')
  const out: string[] = []
  let i = 0
  let listType: 'ul' | 'ol' | null = null

  const closeList = () => {
    if (listType) { out.push(`</${listType}>`); listType = null }
  }

  while (i < lines.length) {
    const line = lines[i]

    // 표: 현재 줄과 다음 줄이 표 구분 패턴
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|?[\s:-]+\|[\s:|-]*$/.test(lines[i + 1])) {
      closeList()
      const tbl: string[] = []
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { tbl.push(lines[i]); i++ }
      out.push(renderTable(tbl))
      continue
    }

    // 수평선
    if (/^\s*---+\s*$/.test(line)) { closeList(); out.push('<hr/>'); i++; continue }

    // 제목
    const h = line.match(/^(#{1,6})\s+(.*)$/)
    if (h) { closeList(); const lv = h[1].length; out.push(`<h${lv}>${inline(h[2])}</h${lv}>`); i++; continue }

    // 인용
    if (/^\s*>\s?/.test(line)) {
      closeList()
      const quote: string[] = []
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { quote.push(lines[i].replace(/^\s*>\s?/, '')); i++ }
      out.push(`<blockquote>${quote.map((q) => inline(q)).join('<br/>')}</blockquote>`)
      continue
    }

    // 순서 목록
    const ol = line.match(/^\s*\d+\.\s+(.*)$/)
    if (ol) { if (listType !== 'ol') { closeList(); out.push('<ol>'); listType = 'ol' } out.push(`<li>${inline(ol[1])}</li>`); i++; continue }

    // 비순서 목록
    const ul = line.match(/^\s*[-*]\s+(.*)$/)
    if (ul) { if (listType !== 'ul') { closeList(); out.push('<ul>'); listType = 'ul' } out.push(`<li>${inline(ul[1])}</li>`); i++; continue }

    // 빈 줄
    if (/^\s*$/.test(line)) { closeList(); i++; continue }

    // 문단
    closeList()
    out.push(`<p>${inline(line)}</p>`)
    i++
  }
  closeList()
  return letterheadHtml + out.join('\n')
}
